import cn from 'classnames';
import { FC, ReactNode, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsOverflowing } from '@/shared/hooks';

import { InlineTextArea } from './InlineText';
import styles from './InstructionLine.module.scss';

type Props = {
  value: string;
  label: string;
  placeholder: string;
  editable: boolean;
  // An instruction of several paragraphs, read in full and edited as text.
  multiline?: boolean;
  // Opens as a text box, e.g. once "+ AI guidance" is chosen elsewhere.
  startEditing?: boolean;
  maxLength?: number;
  className?: string;
  onCommit: (value: string) => void;
  onEditEnd?: () => void;
};

// An AI instruction shown where it applies. Every instruction reaches the
// fill prompt, so an empty one stays visible while the template is edited.
export const InstructionLine: FC<Props> = ({
  value,
  label,
  placeholder,
  editable,
  multiline = false,
  startEditing = false,
  maxLength,
  className,
  onCommit,
  onEditEnd,
}) => {
  const { formatMessage } = useIntl();
  const textId = useId();
  const [editing, setEditing] = useState(startEditing);
  const [expanded, setExpanded] = useState(false);
  const text = value.trim();
  const { isOverflowing, ref } = useIsOverflowing<HTMLSpanElement>(text);

  if (!editable && !text) {
    return null;
  }

  const stopEditing = () => {
    setEditing(false);
    onEditEnd?.();
  };
  const toggle = !multiline &&
    !editing &&
    !!text &&
    (expanded || isOverflowing) && (
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={textId}
        className={cn('p4', styles.more)}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? (
          <FormattedMessage
            id="workspace.preview.showLess"
            defaultMessage="Show less"
          />
        ) : (
          <FormattedMessage
            id="workspace.preview.showMore"
            defaultMessage="Show more"
          />
        )}
      </button>
    );
  const wrap = (content: ReactNode) => (
    <div
      className={cn(styles.root, className)}
      data-component="workspace-template-instruction"
    >
      {content}
      {toggle}
    </div>
  );
  // The label stands on its own row and the text starts under it, read or
  // typed alike, so a click changes nothing but the underline. Long
  // instructions keep their full text in the DOM; a short one's display is
  // clamped.
  // The space after the colon ends the label's row, so the accessible name
  // reads as a sentence and no empty row is drawn.
  const head = <span className={styles.label}>{label}: </span>;
  const shown = text ? (
    <>
      {head}
      <span
        ref={multiline ? undefined : ref}
        id={textId}
        className={cn(
          styles.text,
          multiline || expanded ? styles.full : styles.clamp,
        )}
      >
        {text}
      </span>
    </>
  ) : (
    <span className={styles.text}>{placeholder}</span>
  );

  if (!editable) {
    return wrap(<p className={cn('p2', styles.line)}>{shown}</p>);
  }
  if (editing) {
    return wrap(
      <div className={cn('p2', styles.line, styles.editing)}>
        {head}
        <InlineTextArea
          value={value}
          label={label}
          className={styles.input}
          multiline={multiline}
          maxLength={maxLength}
          title={
            multiline
              ? formatMessage({
                  id: 'workspace.template.multilineHint',
                  defaultMessage:
                    'Enter starts a new line. Ctrl+Enter or a click outside keeps the text, Esc restores it.',
                })
              : undefined
          }
          onCommit={(next) => {
            stopEditing();
            onCommit(next);
          }}
          onCancel={stopEditing}
        />
      </div>,
    );
  }
  return wrap(
    <button
      type="button"
      className={cn('p2', styles.line, styles.button, !text && styles.empty)}
      title={formatMessage({
        id: 'workspace.template.editInstruction',
        defaultMessage: 'Click to edit',
      })}
      onClick={() => setEditing(true)}
    >
      {shown}
    </button>,
  );
};
